import { useTranslation } from 'react-i18next';

import { cn } from '@/styles/tailwind-utils';

interface OnboardingStepperProps {
  currentStep: number; // 1–5; 5 = done (all completed)
}

const STEPS = ['account', 'business', 'services', 'schedule'] as const;

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" className="h-3.5 w-3.5" aria-hidden="true">
      <path
        d="M3 8 L6.5 11.5 L13 5"
        stroke="white"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function OnboardingStepper({ currentStep }: OnboardingStepperProps) {
  const { t } = useTranslation();
  const display = Math.min(currentStep, 5);

  return (
    <nav aria-label={t('onboarding.stepOf', { step: Math.min(display, 4), total: 4 })}>
      <ol className="flex w-full items-start">
        {STEPS.map((key, i) => {
          const stepNumber = i + 1;
          const isCompleted = display > stepNumber;
          const isCurrent = display === stepNumber;
          const isFirst = i === 0;
          const isLast = i === STEPS.length - 1;

          return (
            <li
              key={key}
              className="flex flex-1 flex-col items-center"
              aria-current={isCurrent ? 'step' : undefined}
            >
              {/* Circle row with connectors */}
              <div className="flex w-full items-center">
                {/* Left connector */}
                <div
                  className={cn(
                    'h-px flex-1 transition-colors duration-300',
                    isFirst && 'invisible',
                    !isFirst && (isCompleted || isCurrent) && 'bg-brand',
                    !isFirst && !isCompleted && !isCurrent && 'bg-border',
                  )}
                />

                {/* Step circle */}
                <div
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-all duration-300',
                    isCompleted && 'bg-brand text-white',
                    isCurrent && 'bg-brand text-white shadow-[0_0_0_4px_hsl(var(--color-brand)/0.18)]',
                    !isCompleted && !isCurrent && 'border-2 border-border bg-surface text-muted-foreground',
                  )}
                >
                  {isCompleted ? <CheckIcon /> : <span>{stepNumber}</span>}
                </div>

                {/* Right connector */}
                <div
                  className={cn(
                    'h-px flex-1 transition-colors duration-300',
                    isLast && 'invisible',
                    !isLast && isCompleted && 'bg-brand',
                    !isLast && !isCompleted && 'bg-border',
                  )}
                />
              </div>

              {/* Step label */}
              <span
                className={cn(
                  'mt-2 text-center text-xs font-medium transition-colors duration-300',
                  (isCompleted || isCurrent) ? 'text-brand' : 'text-muted-foreground',
                )}
              >
                {t(`onboarding.stepper.${key}`)}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
